import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Headphones, Shield, MessageCircle, BarChart3 } from "lucide-react";
import { useBranding } from "@/context/BrandingContext";

export default function Login() {
  const { login } = useAuth();
  const { branding, reload: reloadBranding } = useBranding();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  useEffect(() => { const r = sessionStorage.getItem("os_logout_reason"); if (r) { toast.warning(r, { duration: 8000 }); sessionStorage.removeItem("os_logout_reason"); } }, []);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password, remember);
      toast.success("Login realizado com sucesso");
      reloadBranding();
      const back = sessionStorage.getItem("os_redirect"); sessionStorage.removeItem("os_redirect");
      navigate(back || "/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha no login");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50 dark:bg-slate-950">
      {/* Left panel */}
      <div className="hidden lg:flex flex-1 relative bg-gradient-to-br from-emerald-600 via-emerald-700 to-slate-900 p-12 flex-col justify-between overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: "radial-gradient(circle at 20% 20%, white 1px, transparent 1px)",
          backgroundSize: "40px 40px"
        }} />
        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center overflow-hidden">
              {branding.logo_url ? <img src={branding.logo_url} alt="" className="w-full h-full object-contain" data-testid="login-logo" /> : <Headphones className="w-6 h-6 text-white" strokeWidth={2.5} />}
            </div>
            <span className="text-2xl font-extrabold text-white tracking-tight" data-testid="login-system-name">
              {branding.system_name}
            </span>
          </div>
        </div>

        <div className="relative z-10 space-y-6">
          <h2 className="text-4xl lg:text-5xl font-extrabold text-white leading-tight tracking-tight" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>
            Ordens de Serviço integradas ao <span className="text-emerald-300">WhatsApp</span>.
          </h2>
          <p className="text-lg text-emerald-100/90 max-w-md">
            Abra, acompanhe e responda chamados pelo WhatsApp sem sair do painel. Retaguarda completa com dashboard em tempo real.
          </p>
          <div className="grid grid-cols-3 gap-4 pt-4 max-w-md">
            {[
              { icon: MessageCircle, label: "WhatsApp" },
              { icon: BarChart3, label: "Dashboard" },
              { icon: Shield, label: "Seguro" },
            ].map((f, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <f.icon className="w-5 h-5 text-emerald-300 mb-2" />
                <p className="text-sm text-white font-medium">{f.label}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-xs text-emerald-100/60">
          © 2026 {branding.company_name || branding.system_name} — Gestão de Ordens de Serviço
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <Card className="w-full max-w-md p-8 border-slate-200 dark:border-slate-800 shadow-xl">
          <div className="mb-8">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight" style={{ fontFamily: "Plus Jakarta Sans, sans-serif" }}>
              Bem-vindo de volta
            </h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
              Acesse a retaguarda para gerenciar as ordens de serviço.
            </p>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-700 dark:text-slate-300">E-mail</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1.5 h-11"
                placeholder="voce@empresa.com"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700 dark:text-slate-300">Senha</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1.5 h-11"
                placeholder="••••••••"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 cursor-pointer select-none">
              <input type="checkbox" className="w-4 h-4 accent-emerald-600" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="login-remember-checkbox" />
              Manter conectado por 30 dias neste aparelho
            </label>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full h-11 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold active:scale-[0.98] transition-all"
            >
              {loading ? "Entrando..." : "Entrar na retaguarda"}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
